import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["container", "currentTimeLine", "calendarFrame", "headerScroll", "calendarScroll", "loadingSkeleton"]
  static values = {
    date: String,
    hourRange: Array,
    timeZone: String,
    pixelsPerHour: { type: Number, default: 48 },
    turboFrameId: { type: String, default: "" }
  }

  connect() {
    this.updateCurrentTimeLine()
    requestAnimationFrame(() => this.#scrollCurrentTimeIntoViewIfVisible())
    // Update every minute
    this.interval = setInterval(() => {
      this.updateCurrentTimeLine()
    }, 60000) // 60000ms = 1 minute

    // Setup IntersectionObserver for lazy loading calendars
    this.setupIntersectionObserver()

    // Sync scroll between header and calendar containers
    this.syncScroll()

    // Setup drag-to-scroll functionality
    this.setupDragToScroll()

    this.#setupFrameLoadingSkeleton()
  }

  visitSelectUrl(event) {
    const url = event.target?.value
    if (!url) return

    if (this.turboFrameIdValue) {
      Turbo.visit(url, { frame: this.turboFrameIdValue })
    } else {
      Turbo.visit(url)
    }
  }

  disconnect() {
    this.#teardownFrameLoadingSkeleton()
    if (this.interval) {
      clearInterval(this.interval)
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
    // Clean up drag listeners
    this.removeDragListeners()
  }

  #setupFrameLoadingSkeleton() {
    if (!this.hasLoadingSkeletonTarget || !this.turboFrameIdValue) return

    this.boundShowFrameSkeleton = this.#showFrameSkeleton.bind(this)
    this.boundHideFrameSkeleton = this.#hideFrameSkeleton.bind(this)
    this.boundHideFrameSkeletonOnError = this.#hideFrameSkeletonOnError.bind(this)

    document.documentElement.addEventListener("turbo:before-fetch-request", this.boundShowFrameSkeleton)
    document.documentElement.addEventListener("turbo:frame-load", this.boundHideFrameSkeleton)
    document.documentElement.addEventListener("turbo:fetch-request-error", this.boundHideFrameSkeletonOnError)
  }

  #teardownFrameLoadingSkeleton() {
    if (!this.boundShowFrameSkeleton) return

    document.documentElement.removeEventListener("turbo:before-fetch-request", this.boundShowFrameSkeleton)
    document.documentElement.removeEventListener("turbo:frame-load", this.boundHideFrameSkeleton)
    document.documentElement.removeEventListener("turbo:fetch-request-error", this.boundHideFrameSkeletonOnError)

    this.boundShowFrameSkeleton = null
    this.boundHideFrameSkeleton = null
    this.boundHideFrameSkeletonOnError = null
  }

  #turboFrameHeaderFromEvent(event) {
    const headers = event.detail?.fetchOptions?.headers
    if (!headers) return null
    if (typeof headers.get === "function") return headers.get("Turbo-Frame")
    return headers["Turbo-Frame"]
  }

  #showFrameSkeleton(event) {
    if (!this.hasLoadingSkeletonTarget || !this.turboFrameIdValue) return
    const header = this.#turboFrameHeaderFromEvent(event)
    if (header !== this.turboFrameIdValue) return
    this.loadingSkeletonTarget.classList.remove("hidden")
  }

  #hideFrameSkeleton(event) {
    if (!this.hasLoadingSkeletonTarget || !this.turboFrameIdValue) return
    if (event.target?.id !== this.turboFrameIdValue) return
    this.loadingSkeletonTarget.classList.add("hidden")
  }

  #hideFrameSkeletonOnError() {
    if (!this.hasLoadingSkeletonTarget) return
    this.loadingSkeletonTarget.classList.add("hidden")
  }

  setupIntersectionObserver() {
    if (!this.hasCalendarScrollTarget || !this.hasCalendarFrameTarget) {
      return
    }

    const options = {
      root: this.calendarScrollTarget,
      rootMargin: '50px',
      threshold: 0.1
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadCalendar(entry.target)
        }
      })
    }, options)

    // Observe all calendar frames
    this.calendarFrameTargets.forEach(frame => {
      this.intersectionObserver.observe(frame)
    })
  }

  loadCalendar(frame) {
    // Check if already loaded
    if (frame.dataset.loaded === 'true') {
      return
    }

    const url = frame.dataset.calendarUrl
    if (!url) {
      return
    }

    // Mark as loading
    frame.dataset.loaded = 'true'

    // Set src to trigger turbo frame load
    frame.src = url
  }

  syncScroll() {
    // Sync scroll bidirectionally between header and calendar containers
    if (this.hasHeaderScrollTarget && this.hasCalendarScrollTarget) {
      let isScrolling = false

      // Sync calendar scroll to header
      this.calendarScrollTarget.addEventListener('scroll', () => {
        if (!isScrolling) {
          isScrolling = true
          this.headerScrollTarget.scrollLeft = this.calendarScrollTarget.scrollLeft
          requestAnimationFrame(() => {
            isScrolling = false
          })
        }
      })

      // Sync header scroll to calendar
      this.headerScrollTarget.addEventListener('scroll', () => {
        if (!isScrolling) {
          isScrolling = true
          this.calendarScrollTarget.scrollLeft = this.headerScrollTarget.scrollLeft
          requestAnimationFrame(() => {
            isScrolling = false
          })
        }
      })
    }

    // Load initially visible calendars immediately
    if (this.hasCalendarFrameTarget && this.hasCalendarScrollTarget) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.calendarFrameTargets.forEach(frame => {
          const rect = frame.getBoundingClientRect()
          const scrollRect = this.calendarScrollTarget.getBoundingClientRect()
          if (rect.left < scrollRect.right && rect.right > scrollRect.left) {
            this.loadCalendar(frame)
          }
        })
      })
    }
  }

  updateCurrentTimeLine() {
    if (!this.hasCurrentTimeLineTarget) {
      return
    }

    try {
      const hourHeight = this.pixelsPerHourValue
      const todayYmd = this.#ymdInAppZone(new Date())
      // Solo el día seleccionado es "hoy" en la zona del negocio (misma regla que los turnos).
      if (todayYmd !== this.dateValue) {
        this.currentTimeLineTarget.style.display = "none"
        return
      }

      const { hour: currentHour, minute: currentMinute, second: currentSecond } =
        this.#clockInAppZone(new Date())

      const minutesOffset = (currentMinute / 60) * hourHeight
      const secondsOffset = (currentSecond / 3600) * hourHeight
      const totalOffset = minutesOffset + secondsOffset

      const startHourString = this.hourRangeValue[0]
      const startHour = parseInt(startHourString.split(":")[0], 10)
      const hourIndex = currentHour - startHour

      if (
        !Number.isFinite(hourIndex) ||
        hourIndex < 0 ||
        hourIndex >= this.hourRangeValue.length
      ) {
        this.currentTimeLineTarget.style.display = "none"
        return
      }

      const topPosition = (hourIndex * hourHeight) + totalOffset

      this.currentTimeLineTarget.style.display = "block"
      this.currentTimeLineTarget.style.top = `${topPosition}px`
    } catch (_e) {
      this.currentTimeLineTarget.style.display = "none"
    }
  }

  /** Calendar date YYYY-MM-DD in the app timezone (Rails Time.zone). */
  #ymdInAppZone(date) {
    const d = new Date(date)
    if (!this.hasTimeZoneValue || !this.timeZoneValue) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      return `${y}-${m}-${day}`
    }
    try {
      return new Intl.DateTimeFormat("sv-SE", {
        timeZone: this.timeZoneValue,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(d)
    } catch (_e) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      return `${y}-${m}-${day}`
    }
  }

  /** Wall clock in the app timezone — must match booking / grid times. */
  #clockInAppZone(date) {
    const d = new Date(date)
    if (!this.hasTimeZoneValue || !this.timeZoneValue) {
      return {
        hour: d.getHours(),
        minute: d.getMinutes(),
        second: d.getSeconds()
      }
    }
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: this.timeZoneValue,
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hourCycle: "h23"
      }).formatToParts(d)
      const hourPart = parts.find((p) => p.type === "hour")
      const minutePart = parts.find((p) => p.type === "minute")
      const secondPart = parts.find((p) => p.type === "second")
      if (!hourPart || !minutePart || !secondPart) {
        throw new Error("missing time parts")
      }
      return {
        hour: parseInt(hourPart.value, 10),
        minute: parseInt(minutePart.value, 10),
        second: parseInt(secondPart.value, 10)
      }
    } catch (_e) {
      return {
        hour: d.getHours(),
        minute: d.getMinutes(),
        second: d.getSeconds()
      }
    }
  }

  #scrollCurrentTimeIntoViewIfVisible() {
    if (!this.hasCurrentTimeLineTarget) {
      return
    }
    if (this.currentTimeLineTarget.style.display === "none") {
      return
    }
    this.currentTimeLineTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  setupDragToScroll() {
    // Setup drag-to-scroll for calendar container
    if (this.hasCalendarScrollTarget) {
      this.setupDragForElement(this.calendarScrollTarget)
    }
    // Setup drag-to-scroll for header container
    if (this.hasHeaderScrollTarget) {
      this.setupDragForElement(this.headerScrollTarget)
    }
  }

  setupDragForElement(element) {
    let isDown = false
    let startX
    let scrollLeft
    let hasDragged = false

    element.addEventListener('mousedown', (e) => {
      isDown = true
      hasDragged = false
      element.style.cursor = 'grabbing'
      element.style.userSelect = 'none'
      startX = e.pageX - element.offsetLeft
      scrollLeft = element.scrollLeft
      // Mark that drag has started
      element.dataset.isDragging = 'true'
    })

    element.addEventListener('mouseleave', () => {
      if (isDown && hasDragged) {
        // Delay clearing the flag to prevent click events from opening modal
        setTimeout(() => {
          element.dataset.isDragging = 'false'
        }, 100)
      } else {
        element.dataset.isDragging = 'false'
      }
      isDown = false
      element.style.cursor = 'grab'
      element.style.userSelect = ''
    })

    element.addEventListener('mouseup', () => {
      if (hasDragged) {
        // Delay clearing the flag to prevent click events from opening modal
        setTimeout(() => {
          element.dataset.isDragging = 'false'
        }, 100)
      } else {
        element.dataset.isDragging = 'false'
      }
      isDown = false
      element.style.cursor = 'grab'
      element.style.userSelect = ''
    })

    element.addEventListener('mousemove', (e) => {
      if (!isDown) return
      const x = e.pageX - element.offsetLeft
      const walk = (x - startX) * 2 // Scroll speed multiplier

      // Check if mouse has moved significantly (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDragged = true
        e.preventDefault()
        element.scrollLeft = scrollLeft - walk
      }
    })

    // Set initial cursor style
    element.style.cursor = 'grab'
    element.dataset.isDragging = 'false'
  }

  removeDragListeners() {
    // Event listeners are automatically cleaned up when element is removed
    // But we can reset cursor styles if needed
    if (this.hasCalendarScrollTarget) {
      this.calendarScrollTarget.style.cursor = ''
      this.calendarScrollTarget.style.userSelect = ''
    }
    if (this.hasHeaderScrollTarget) {
      this.headerScrollTarget.style.cursor = ''
      this.headerScrollTarget.style.userSelect = ''
    }
  }
}
